import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { getPaymentStatus } from "../lib/api";

const MONITORING_URL = "https://app.myfreescorenow.com/enroll/C02B9131?s=MXxmYWxzZQ%3D%3D";
const CREDIT_REPAIR = new Set(["bgm_monthly", "bgm_starter", "bgm_standard", "bgm_vip_elite", "bgm_transformation"]);

export default function PaymentResult() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const loc = useLocation();
  const isCancel = loc.pathname.includes("cancel");
  const sessionId = params.get("session_id");
  const [state, setState] = useState(isCancel ? "cancelled" : "polling");
  const [plan, setPlan] = useState("");
  const [needsMonitoring, setNeedsMonitoring] = useState(false);
  const [countdown, setCountdown] = useState(6);

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const d = await getPaymentStatus(sessionId);
        setPlan(d.plan || "");
        if (d.payment_status === "paid") {
          setState("paid");
          if (CREDIT_REPAIR.has(d.lookup_key)) setNeedsMonitoring(true);
          return;
        }
        if (["failed", "expired"].includes(d.payment_status)) { setState("failed"); return; }
      } catch {}
      if (++tries > 10) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, isCancel]);

  useEffect(() => {
    if (!needsMonitoring) return;
    if (countdown <= 0) { window.location.href = MONITORING_URL; return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [needsMonitoring, countdown]);

  const Icon = state === "paid" ? CheckCircle2 : state === "polling" ? Loader2 : XCircle;
  const title = {
    polling: "Confirming your payment…",
    paid: "Enrollment confirmed",
    failed: "Payment not completed",
    timeout: "Still processing",
    cancelled: "Checkout cancelled",
  }[state];
  const body = {
    polling: "Please wait a moment while we verify with Stripe.",
    paid: `Welcome to BGM${plan ? ` — ${plan}` : ""}! A BGM strategist will reach out with your onboarding steps shortly.`,
    failed: "Your payment didn't go through. You can try again anytime.",
    timeout: "Your payment is taking longer than usual. If you were charged, you're all set — we'll confirm by email.",
    cancelled: "No charge was made. Whenever you're ready, your plan is waiting.",
  }[state];

  return (
    <div className="grain-none flex min-h-screen items-center justify-center bg-[#050505] px-6">
      <div className="glass-solid gold-border-glow w-full max-w-md rounded-3xl p-10 text-center" data-testid="payment-result">
        <Icon className={`mx-auto mb-6 h-16 w-16 text-[#d4af37] ${state === "polling" ? "animate-spin" : ""}`} data-testid={`payment-${state}`} />
        <h1 className="font-display text-2xl text-[#f5f5f5]">{title}</h1>
        <p className="mt-4 font-body text-sm leading-relaxed text-[#a3a3a3]">{body}</p>

        {needsMonitoring && (
          <div className="mt-6 rounded-2xl border border-[#d4af37]/40 bg-[#d4af37]/10 p-5 text-left" data-testid="monitoring-required">
            <p className="font-body text-sm font-semibold text-gold">Required next step</p>
            <p className="mt-1 font-body text-sm text-[#c9c9c9]">
              Active credit monitoring is required for every credit repair client. Enroll now to activate your service.
            </p>
            <a href={MONITORING_URL} target="_blank" rel="noopener noreferrer" data-testid="monitoring-btn"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#d4af37] px-6 py-3 font-body text-sm font-semibold text-[#050505] hover:bg-[#f3e5ab]">
              Enroll in credit monitoring
            </a>
            <p className="mt-3 text-center font-body text-xs text-[#888]">Redirecting you automatically in {countdown}s…</p>
          </div>
        )}

        <button onClick={() => nav("/")} data-testid="payment-home-btn"
          className="mt-6 rounded-full border border-white/15 px-7 py-3 font-body text-sm font-semibold text-[#c9c9c9] hover:border-[#d4af37]/50 hover:text-gold">
          Back to BGM
        </button>
      </div>
    </div>
  );
}
